import { useMutation, useQuery } from '@tanstack/react-query';
import { APITypes } from '../../primary-record-types';
import { appAPI } from '../../utils/api';
import { getIntl } from '../../utils/intl';
import urls from '../../utils/urls';
import { getMyOrganizationInvitationsCacheKey, onUpdateMyOrganizationInvitation } from './cacheKeys';
import { useQueryErrorNotify } from '../useQueryCallbacks';
import useNotify from '../useNotify';

const intl = getIntl('useMyOrganizationInvitations', {
    invitationLoadFailure: 'Failed to load organization invitations',
    invitationResponseFailure: 'Failed to respond to invitation',
    acceptInvitationSuccess: 'Invitation accepted',
    declineInvitationSuccess: 'Invitation declined',
});

export type UseMyOrganizationInvitationsArgs = {
    poll?: boolean;
    onAcceptInvitation?: () => void;
};

export type InvitationResponseArgs = {
    invitationId: string;
    response: APITypes.organizationService.OrganizationUserInvitationResponseRequest['response'];
    organizationId: string;
};

export type UseMyOrganizationInvitationsResult = {
    isLoadingMyOrganizationInvitation: boolean;
    myOrganizationInvitation: APITypes.organizationService.GetOrganizationUserInvitationMeResponse['items'][0] | null;
    respondToInvitation: (args: InvitationResponseArgs) => void;
    isRespondingToInvitation: boolean;
};

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

export default function useMyOrganizationInvitations({
    poll,
    onAcceptInvitation,
}: UseMyOrganizationInvitationsArgs = {}): UseMyOrganizationInvitationsResult {
    const notify = useNotify();
    const myInvitationsQuery = useQuery({
        queryKey: getMyOrganizationInvitationsCacheKey(),
        queryFn: async () => {
            return await appAPI.get<APITypes.organizationService.GetOrganizationUserInvitationMeResponse>(
                urls.api.organizations.userInvitations.myInvitations,
            );
        },
        refetchInterval: poll ? 3000 : false,
    });
    useQueryErrorNotify(myInvitationsQuery, intl('invitationLoadFailure'));

    const respondToInvitationMutation = useMutation({
        mutationFn: async (args: InvitationResponseArgs) => {
            const body: APITypes.organizationService.OrganizationUserInvitationResponseRequest = {
                response: args.response,
            };
            await appAPI.post<APITypes.organizationService.OrganizationUserInvitationResponseRequest>(
                urls.api.organizations.userInvitations.inviteResponse(args.organizationId, args.invitationId),
                { body },
            );
            // Delay to allow the server to propagate join changes
            if (args.response === 'accept') {
                await sleep(1000);
            }
        },
        onSuccess: (_data, args) => {
            onUpdateMyOrganizationInvitation.resetCache({})();
            if (args.response === 'accept') {
                onAcceptInvitation?.();
            }

            notify(
                'success',
                intl(args.response === 'accept' ? 'acceptInvitationSuccess' : 'declineInvitationSuccess'),
            );
        },
        onError: () => {
            notify('error', intl('invitationResponseFailure'));
        },
    });

    return {
        isLoadingMyOrganizationInvitation: myInvitationsQuery.isLoading,
        myOrganizationInvitation: myInvitationsQuery.data?.items?.[0] || null,
        respondToInvitation: respondToInvitationMutation.mutate,
        isRespondingToInvitation: respondToInvitationMutation.isPending,
    };
}
